"use client";

import type { ComponentType, CSSProperties, ReactNode } from "react";
import { cn } from "@carpybara/ui/shadcn/utils";

import { PageScene, type PageSceneKind } from "./home-glow/page-scene";

export type PlanId = "free" | "pro" | "ultra";

type IconComponent = ComponentType<{ className?: string; "aria-hidden"?: boolean }>;

const SCENE_KIND: Record<PlanId, PageSceneKind> = {
  free: "planFree",
  pro: "planPro",
  ultra: "planUltra",
};

/** What each plan's icon badge and feature tiles are tinted with (Pro rides the brand amber, Ultra the theme's purple). */
const BADGE_TONE: Record<PlanId, string> = {
  free: "border-(--border) bg-(--bg-card)/80 text-(--text-secondary)",
  pro: "border-(--accent)/30 bg-(--accent-bg) text-(--accent-text)",
  ultra: "border-purple-500/35 bg-purple-500/15 text-purple-400",
};
const TILE_TONE: Record<PlanId, string> = {
  free: "bg-(--bg-secondary) text-(--text-muted)",
  pro: "bg-(--accent)/15 text-(--accent)",
  ultra: "bg-purple-500/15 text-purple-400",
};

/**
 * The animated header of a plan card: that plan's own glow scene (what it lets you do) on a plate of light, drawn by the
 * same iso scenes as the home page, with the one line under it that says what the scene shows. The box keeps its 3:2
 * shape from the first paint, and the scene's code is fetched only when the box is near the viewport and the page is
 * idle, so the plans never move when it arrives.
 */
export function PlanScene({ plan, icon: Icon, caption }: { plan: PlanId; icon: IconComponent; caption: string }) {
  return (
    <>
      <div className="home-scene plan-scene" data-plan={plan}>
        <PageScene kind={SCENE_KIND[plan]} fill idle />
        <span
          className={cn(
            "pointer-events-none absolute start-3 top-3 inline-flex size-10 items-center justify-center rounded-xl border backdrop-blur-sm",
            BADGE_TONE[plan],
          )}
        >
          <Icon className="size-[18px]" aria-hidden />
        </span>
      </div>
      <p className="plan-scene-caption">{caption}</p>
    </>
  );
}

export type PlanFeatureItem = { icon: IconComponent; text: ReactNode };

/** A plan's feature list: each line with the pictogram of what it is, easing in one after another. */
export function PlanFeatureList({ plan, features }: { plan: PlanId; features: readonly PlanFeatureItem[] }) {
  return (
    <ul className="space-y-2.5 text-sm text-(--text-muted)">
      {features.map((feature, index) => {
        const Icon = feature.icon;
        return (
          <li key={index} className="plan-feature flex items-start gap-2.5" style={{ "--i": index } as CSSProperties}>
            <span className={cn("mt-px grid size-6 shrink-0 place-items-center rounded-lg", TILE_TONE[plan])}>
              <Icon className="size-3.5" aria-hidden />
            </span>
            <span className="pt-0.5 leading-snug">{feature.text}</span>
          </li>
        );
      })}
    </ul>
  );
}
